import { ApplicationElement, html } from '@crossiety/lit-components';
import CommonActions from '@crossiety/lit-components/components/common_actions/element.js';
import AutoHeightTextarea from '@crossiety/lit-components/lib/auto_height_textarea.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Comment and comment reply form
 *
 * @prop cardId - Id of the card the form belongs to
 * @prop parentId - Id of the comment we are replying to
 */

export default class CommentForm extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    cardId: { type: String },
    parentId: { type: String },
    hasContent: { type: Boolean, state: true },
  };

  constructor() {
    super();
    /** @internal */
    this.commonActions = new CommonActions(this);
  }

  /** @internal */
  static translations = translations;

  /** @internal */
  get isReply() {
    return this.parentId;
  }

  /** @internal */
  get formAction() {
    return `/cards/${this.cardId}/comments`;
  }

  /** @internal */
  get formPlaceholder() {
    return this.isReply ? this.t('add_a_reply') : this.t('add_a_comment');
  }

  /** @internal */
  get form() {
    return this.shadowRoot.querySelector('form');
  }

  /** @internal */
  get textarea() {
    return this.shadowRoot.querySelector('textarea');
  }

  /** @internal */
  get submitOnEnter() {
    return !window.AppConfig?.runtime?.isMobileDevice;
  }

  /** @internal */
  get isSubmittable() {
    return this.hasContent;
  }

  /** @internal */
  get isDisabled() {
    return !this.isSubmittable;
  }

  firstUpdated() {
    super.firstUpdated();
    this.autoHeightTextarea = new AutoHeightTextarea(this.textarea);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.autoHeightTextarea.destroy();
  }

  isValid(formData) {
    const bodyPlain = formData.get('card_comment[body]') || '';

    return bodyPlain.trim().length;
  }

  async onSubmit(e) {
    e.preventDefault();

    const formData = new FormData(e.target);

    if (!this.isValid(formData)) return;

    try {
      await this.commonActions.post(this.formAction, {
        body: formData,
      });
      this.form.reset();
      this.textarea.focus();
      this.updateHasContent();
      this.dispatchEvent(
        new Event('xs-comment-form-submit', { bubbles: true, composed: true })
      );
    } catch {} // eslint-disable-line no-empty
  }

  onReset() {
    // In case user manually resized the textarea
    this.textarea.removeAttribute('style');
    requestAnimationFrame(() => this.updateHasContent());
  }

  onInput() {
    this.updateHasContent();
  }

  onEnter(e) {
    if (e.key === 'Enter' && !e.shiftKey && this.submitOnEnter) {
      e.preventDefault();
      this.form.requestSubmit();
    }
  }

  updateHasContent() {
    this.hasContent = this.textarea.value.trim().length > 0;
  }

  render() {
    return html`
      <form @submit=${this.onSubmit} @reset=${this.onReset}>
        ${this.parentId &&
        html`<input
          class="form-control"
          type="hidden"
          name="card_comment[parent_id]"
          value=${this.parentId} />`}

        <textarea
          @keydown=${this.onEnter}
          @input=${this.onInput}
          class="input-control"
          type="text"
          name="card_comment[body]"
          placeholder=${this.formPlaceholder}></textarea>

        <button
          class="xs-btn btn-primary xs-btn-rounded"
          title=${this.t('submit')}
          ?disabled=${this.isDisabled}>
          <ef-icon name="arrow-up"></ef-icon>
        </button>

        <button
          class="xs-btn btn-secondary xs-btn-rounded"
          title=${this.t('cancel')}
          type="reset"
          ?hidden=${!this.hasContent}>
          <ef-icon name="close"></ef-icon>
        </button>
      </form>
    `;
  }
}

CommentForm.registerTagName('xs-comment-form');

customElements.get('xs-comment-form') ||
  customElements.define('xs-comment-form', CommentForm);
